import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
    X, FileText, MapPin, User, Euro, Calendar,
    Home, ArrowRight, Clock, CheckCircle, Send,
    FileCheck, Download, Mail, Percent, Ruler,
    ThermometerSun, ChevronRight
} from 'lucide-react';

const STATUS_CONFIG = {
    draft: { label: 'Brouillon', color: 'bg-slate-500/20 text-slate-400', icon: Clock },
    generated: { label: 'PDF généré', color: 'bg-blue-500/20 text-blue-400', icon: FileCheck },
    sent: { label: 'Envoyé', color: 'bg-amber-500/20 text-amber-400', icon: Send },
    acknowledged: { label: 'Validé', color: 'bg-green-500/20 text-green-400', icon: CheckCircle }
};

const TYPE_LABELS = { simple: 'Simple', exclusif: 'Exclusif', semi_exclusif: 'Semi-exclusif' };
const TYPE_COLORS = {
    simple: 'border-slate-500/40 text-slate-400 bg-slate-500/10',
    exclusif: 'border-[#FF914D]/40 text-[#FF914D] bg-[#FF914D]/10',
    semi_exclusif: 'border-[#681793]/40 text-[#681793] bg-[#681793]/10'
};

const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const DPE_COLORS = {
    A: 'bg-green-500', B: 'bg-lime-500', C: 'bg-yellow-400',
    D: 'bg-amber-500', E: 'bg-orange-500', F: 'bg-red-500', G: 'bg-red-700'
};

export default function MandatePreviewPanel({ mandateId, onClose, basePath }) {
    const [mandate, setMandate] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!mandateId) return;
        setLoading(true);
        api.get(`/mandates-v2/${mandateId}`)
            .then(res => setMandate(res.data))
            .catch(() => setMandate(null))
            .finally(() => setLoading(false));
    }, [mandateId]);

    if (!mandateId) return null;

    const statusConfig = STATUS_CONFIG[mandate?.status] || STATUS_CONFIG.draft;
    const StatusIcon = statusConfig?.icon;

    return (
        <AnimatePresence>
            {mandateId && (
                <>
                    {/* Overlay */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
                        onClick={onClose}
                        data-testid="panel-overlay"
                    />
                    
                    {/* Panel */}
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                        className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-[#0c0c14] border-l border-border z-50 overflow-y-auto shadow-2xl"
                        data-testid="mandate-preview-panel"
                    >
                        {/* Header */}
                        <div className="sticky top-0 bg-[#0c0c14]/95 backdrop-blur-sm border-b border-border p-4 flex items-center justify-between z-10">
                            <h2 className="text-lg font-bold text-white flex items-center gap-2">
                                <FileText className="w-5 h-5 text-[#681793]" />
                                Aperçu mandat
                            </h2>
                            <Button variant="ghost" size="icon" onClick={onClose} className="text-muted-foreground hover:text-white" data-testid="close-panel-button">
                                <X className="w-5 h-5" />
                            </Button>
                        </div>

                        {loading ? (
                            <div className="p-6 space-y-4">
                                <Skeleton className="h-8 w-48 bg-muted" />
                                <Skeleton className="h-24 w-full bg-muted rounded-xl" />
                                <Skeleton className="h-24 w-full bg-muted rounded-xl" />
                                <Skeleton className="h-24 w-full bg-muted rounded-xl" />
                            </div>
                        ) : !mandate ? (
                            <div className="p-6 text-center text-muted-foreground">Mandat introuvable</div>
                        ) : (
                            <div className="p-4 space-y-4">
                                {/* Title + Status */}
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-xl font-bold text-white">{mandate.mandate_number}</span>
                                        <Badge className={`${TYPE_COLORS[mandate.mandate_type]} border text-xs`}>
                                            {TYPE_LABELS[mandate.mandate_type]}
                                        </Badge>
                                    </div>
                                    <Badge className={`${statusConfig.color} border-0 flex items-center gap-1 w-fit`}>
                                        <StatusIcon className="w-3.5 h-3.5" />
                                        {statusConfig.label}
                                    </Badge>
                                </div>

                                {/* Price Card */}
                                <div className="bg-gradient-to-br from-[#681793]/20 to-[#FF914D]/20 rounded-xl p-4 border border-white/5">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Prix HAI</p>
                                            <p className="text-xl font-bold text-white">{formatCurrency(mandate.pricing?.sale_price)}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Net vendeur</p>
                                            <p className="text-xl font-bold text-green-400">{formatCurrency(mandate.pricing?.net_seller)}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Commission</p>
                                            <p className="text-sm font-semibold text-[#FF914D] flex items-center gap-1">
                                                <Percent className="w-3 h-3" />
                                                {mandate.pricing?.commission_rate || '-'}%
                                                <span className="text-white/50 text-xs ml-1">
                                                    ({formatCurrency(mandate.pricing?.commission_amount)})
                                                </span>
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Honoraires</p>
                                            <p className="text-sm font-semibold text-muted-foreground">
                                                {mandate.pricing?.fees_paid_by === 'buyer' ? 'Charge acquéreur' : 'Charge vendeur'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Property */}
                                {mandate.property && (
                                    <div className="bg-card rounded-xl p-4 border border-border">
                                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                                            <Home className="w-4 h-4 text-[#FF914D]" />
                                            Bien immobilier
                                        </h3>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-start gap-2">
                                                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                                                <span className="text-white/80">
                                                    {mandate.property.address}, {mandate.property.postal_code} {mandate.property.city}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-3 gap-2 mt-3">
                                                {mandate.property.surface && (
                                                    <div className="bg-white/5 rounded-lg p-2 text-center">
                                                        <Ruler className="w-3.5 h-3.5 text-muted-foreground mx-auto mb-0.5" />
                                                        <p className="text-xs font-semibold text-white">{mandate.property.surface} m²</p>
                                                    </div>
                                                )}
                                                {mandate.property.rooms && (
                                                    <div className="bg-white/5 rounded-lg p-2 text-center">
                                                        <Home className="w-3.5 h-3.5 text-muted-foreground mx-auto mb-0.5" />
                                                        <p className="text-xs font-semibold text-white">{mandate.property.rooms} pièces</p>
                                                    </div>
                                                )}
                                                {mandate.property.dpe_rating && (
                                                    <div className="bg-white/5 rounded-lg p-2 text-center">
                                                        <ThermometerSun className="w-3.5 h-3.5 text-muted-foreground mx-auto mb-0.5" />
                                                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold text-white ${DPE_COLORS[mandate.property.dpe_rating] || 'bg-gray-500'}`}>
                                                            DPE {mandate.property.dpe_rating}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Sellers */}
                                {mandate.sellers?.length > 0 && (
                                    <div className="bg-card rounded-xl p-4 border border-border">
                                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                                            <User className="w-4 h-4 text-[#681793]" />
                                            Vendeur{mandate.sellers.length > 1 ? 's' : ''}
                                        </h3>
                                        <div className="space-y-2">
                                            {mandate.sellers.map((seller, i) => (
                                                <div key={i} className="flex items-center gap-3 bg-white/5 rounded-lg p-2.5">
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white text-xs font-bold shrink-0">
                                                        {seller.first_name?.[0]}{seller.last_name?.[0]}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium text-white truncate">{seller.first_name} {seller.last_name}</p>
                                                        {seller.email && <p className="text-xs text-muted-foreground truncate">{seller.email}</p>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Dates */}
                                <div className="bg-card rounded-xl p-4 border border-border">
                                    <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-blue-400" />
                                        Dates
                                    </h3>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Création</span>
                                            <span className="text-white">{formatDate(mandate.created_at)}</span>
                                        </div>
                                        {mandate.start_date && (
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Début</span>
                                                <span className="text-white">{formatDate(mandate.start_date)}</span>
                                            </div>
                                        )}
                                        {mandate.end_date && (
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Fin</span>
                                                <span className="text-white">{formatDate(mandate.end_date)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Action Button */}
                                <Link to={`${basePath}/mandates/v2/${mandateId}`} className="block">
                                    <Button className="w-full bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white h-11" data-testid="open-mandate-button">
                                        Ouvrir le mandat complet
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
